/**
 * System Dashboard
 * 
 * Consolidated system monitoring:
 * - User error rates and trends
 * - System health status
 * - Sentry error triage with agent-consumable export
 * - Quick actions for common issues
 * - CSV export for audit logs
 * 
 * Uses dark glassmorphism design system.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { adminGlassStyles } from '@/lib/design';
import { toast } from 'sonner';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Clock,
  Users,
  Zap,
  Server,
  Wifi,
  TrendingUp,
  TrendingDown,
  Archive,
  AlertCircle,
  Image,
  Loader,
  Download,
  Shield,
  Copy,
  ExternalLink,
  Bug,
} from 'lucide-react';
import type { TriagedIssue, TriageSummary } from '@/services/sentryTriageService';
import { fetchSentryTriage, copyAgentJson } from '@/services/sentryTriageService';

// =============================================================================
// TYPES
// =============================================================================

interface ErrorStats {
  total_errors: number;
  page_crashes: number;
  api_failures: number;
  auth_issues: number;
  media_errors: number;
  load_failures: number;
  network_errors: number;
  unique_users_affected: number;
}

interface RecentError {
  id: string;
  error_type: string;
  error_message: string;
  error_stack?: string | null;
  error_code?: string | null;
  page_url: string | null;
  component?: string | null;
  action?: string | null;
  user_id?: string | null;
  user_agent?: string | null;
  screen_size?: string | null;
  connection_type?: string | null;
  status: string | null;
  created_at: string;
}

interface HealthCheck {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  response_time_ms?: number;
  last_checked: string;
}

interface DbCheckResult {
  name: string;
  status: 'pass' | 'warn' | 'fail';
  expected?: unknown;
  actual?: unknown;
  details?: string;
}

interface DbHealthReport {
  postgres_version: string;
  timestamp: string;
  overall: 'healthy' | 'degraded' | 'critical';
  checks: DbCheckResult[];
  baseline: Record<string, number>;
}

// =============================================================================
// SENTRY TRIAGE TAB
// =============================================================================

const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-[#FF453A]/20', text: 'text-[#FF453A]', border: 'border-[#FF453A]/30' },
  high:     { bg: 'bg-[#FF9F0A]/20', text: 'text-[#FF9F0A]', border: 'border-[#FF9F0A]/30' },
  medium:   { bg: 'bg-[#FFD60A]/20', text: 'text-[#FFD60A]', border: 'border-[#FFD60A]/30' },
  low:      { bg: 'bg-white/10',     text: 'text-white/50',   border: 'border-white/10' },
};

const CATEGORY_LABELS: Record<string, string> = {
  stale_deployment: 'Stale Deploy',
  null_reference: 'Null Ref',
  network: 'Network',
  auth: 'Auth',
  database: 'Database',
  benign: 'Benign',
  billing: 'Billing',
  media: 'Media',
  unknown: 'Unknown',
};

function SentryTriagePanel() {
  const [issues, setIssues] = useState<TriagedIssue[]>([]);
  const [summary, setSummary] = useState<TriageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchSentryTriage();
      setIssues(resp.issues);
      setSummary(resp.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Sentry issues');
    } finally {
      setLoading(false);
    }
  }, []);

  const filtered = issues.filter((i) => {
    if (categoryFilter !== 'all' && i.category !== categoryFilter) return false;
    if (priorityFilter !== 'all' && i.priority !== priorityFilter) return false;
    return true;
  });

  const handleExport = () => {
    copyAgentJson(filtered).then(() => {
      toast.success(`Copied ${filtered.length} issues to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const handleCopyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt).then(() => {
      toast.success('Agent prompt copied');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatTimeAgo = (dateString: string) => {
    const diffMs = Date.now() - new Date(dateString).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const categories = Array.from(new Set(issues.map(i => i.category)));

  return (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {(['critical', 'high', 'medium', 'low'] as const).map((level) => {
            const colors = PRIORITY_COLORS[level];
            return (
              <button
                key={level}
                onClick={() => setPriorityFilter(priorityFilter === level ? 'all' : level)}
                className={`p-4 rounded-2xl border-2 transition-all text-left ${
                  priorityFilter === level
                    ? `${colors.bg} ${colors.border}`
                    : 'border-transparent bg-white/5 hover:bg-white/10'
                }`}
              >
                <p className={`text-2xl font-bold ${colors.text}`}>
                  {summary[level]}
                </p>
                <p className="text-xs text-white/60 capitalize">{level}</p>
              </button>
            );
          })}
          <div className="p-4 rounded-2xl bg-white/5 text-left">
            <p className="text-2xl font-bold text-white">{summary.total}</p>
            <p className="text-xs text-white/60">Total</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={loadIssues}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#0A84FF]/20 hover:bg-[#0A84FF]/30 text-[#0A84FF] rounded-xl font-medium transition-all disabled:opacity-50 border border-[#0A84FF]/30"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : issues.length === 0 ? 'Fetch Issues' : 'Refresh'}
        </button>

        {issues.length > 0 && (
          <>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-[#0A84FF]/50 outline-none"
            >
              <option value="all">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
              ))}
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-[#0A84FF]/50 outline-none"
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <div className="ml-auto">
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-[#30D158]/20 hover:bg-[#30D158]/30 text-[#30D158] rounded-xl font-medium transition-all border border-[#30D158]/30"
              >
                <Copy className="w-4 h-4" />
                Copy for Agent ({filtered.length})
              </button>
            </div>
          </>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="p-3 bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-xl text-[#FF453A] text-sm mb-4">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && issues.length === 0 && (
        <div className="py-16 text-center">
          <Bug className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/60">Click &quot;Fetch Issues&quot; to load Sentry issues</p>
        </div>
      )}

      {/* Issue List */}
      {filtered.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={adminGlassStyles.card}>
          <div className="divide-y divide-white/10">
            {filtered.map((issue) => {
              const colors = PRIORITY_COLORS[issue.priority];
              const expanded = expandedId === issue.id;
              return (
                <div key={issue.id} className="hover:bg-white/5 transition-colors">
                  <button
                    className="w-full p-4 text-left"
                    onClick={() => setExpandedId(expanded ? null : issue.id)}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${colors.bg} ${colors.border} border`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium line-clamp-1">{issue.title}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} ${colors.border} border font-medium capitalize`}>
                            {issue.priority}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                            {CATEGORY_LABELS[issue.category] || issue.category}
                          </span>
                          <span className="text-xs text-white/40 flex items-center gap-1">
                            <Users className="w-3 h-3" /> {issue.userCount}
                          </span>
                          <span className="text-xs text-white/40">{issue.count} events</span>
                          <span className="text-xs text-white/40 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatTimeAgo(issue.lastSeen)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4 pl-10 space-y-3">
                      <div className="p-3 bg-white/5 rounded-xl text-sm">
                        <p className="text-white/70 mb-1"><strong className="text-white/90">Culprit:</strong> {issue.culprit}</p>
                        {issue.affectedFile && (
                          <p className="text-white/70 mb-1"><strong className="text-white/90">File:</strong> <code className="text-[#0A84FF]">{issue.affectedFile}</code></p>
                        )}
                        <p className="text-white/70 mb-1"><strong className="text-white/90">Suggested:</strong> {issue.suggestedAction}</p>
                        <p className="text-white/70"><strong className="text-white/90">Score:</strong> {issue.priorityScore}/100</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={issue.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-sm text-white/80 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> View in Sentry
                        </a>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCopyPrompt(issue.agentPrompt); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#5E5CE6]/20 hover:bg-[#5E5CE6]/30 rounded-lg text-sm text-[#5E5CE6] transition-colors border border-[#5E5CE6]/30"
                        >
                          <Copy className="w-3.5 h-3.5" /> Copy Agent Prompt
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {summary && (
        <p className="text-xs text-white/30 mt-3">
          Top category: {CATEGORY_LABELS[summary.topCategory] || summary.topCategory} · Last updated {new Date(summary.lastUpdated).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

type SystemTab = 'overview' | 'sentry';

export function SystemDashboard() {
  const [activeTab, setActiveTab] = useState<SystemTab>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  // Data state
  const [todayStats, setTodayStats] = useState<ErrorStats | null>(null);
  const [yesterdayStats, setYesterdayStats] = useState<ErrorStats | null>(null);
  const [recentErrors, setRecentErrors] = useState<RecentError[]>([]);
  const [newErrorCount, setNewErrorCount] = useState(0);
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [dbHealth, setDbHealth] = useState<DbHealthReport | null>(null);
  const [dbHealthLoading, setDbHealthLoading] = useState(false);
  const [dbHealthError, setDbHealthError] = useState<string | null>(null);
  
  // Filter state
  const [errorFilter, setErrorFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('new');

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  const fetchDashboardData = useCallback(async () => {
    setRefreshing(true);
    
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const { data: todayErrors } = await supabase
        .from('user_error_log')
        .select('error_type')
        .gte('created_at', today.toISOString())
        .lt('created_at', tomorrow.toISOString());

      const { data: yestErrors } = await supabase
        .from('user_error_log')
        .select('error_type')
        .gte('created_at', yesterday.toISOString())
        .lt('created_at', today.toISOString());

      const { count: newCount } = await supabase
        .from('user_error_log')
        .select('id', { count: 'exact' })
        .eq('status', 'new');

      let query = supabase
        .from('user_error_log')
        .select('id, error_type, error_message, error_stack, error_code, page_url, component, action, user_id, user_agent, screen_size, connection_type, status, created_at')
        .order('created_at', { ascending: false })
        .limit(20);

      if (errorFilter !== 'all') {
        query = query.eq('error_type', errorFilter);
      }
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data: errors } = await query;

      const calcStats = (errs: { error_type: string }[] | null): ErrorStats => {
        if (!errs) return {
          total_errors: 0, page_crashes: 0, api_failures: 0, auth_issues: 0,
          media_errors: 0, load_failures: 0, network_errors: 0, unique_users_affected: 0
        };
        return {
          total_errors: errs.length,
          page_crashes: errs.filter(e => e.error_type === 'page_crash').length,
          api_failures: errs.filter(e => e.error_type === 'api_failure').length,
          auth_issues: errs.filter(e => e.error_type === 'auth_issue').length,
          media_errors: errs.filter(e => e.error_type === 'media_error').length,
          load_failures: errs.filter(e => e.error_type === 'load_failure').length,
          network_errors: errs.filter(e => e.error_type === 'network_error').length,
          unique_users_affected: 0,
        };
      };

      setTodayStats(calcStats(todayErrors));
      setYesterdayStats(calcStats(yestErrors));
      setNewErrorCount(newCount || 0);
      setRecentErrors(errors || []);

      // Basic service health checks (quick pings)
      const dbStart = Date.now();
      const { error: dbPing } = await supabase.from('profiles').select('id', { count: 'exact', head: true });
      const dbTime = Date.now() - dbStart;

      const authStart = Date.now();
      const { error: authPing } = await supabase.auth.getSession();
      const authTime = Date.now() - authStart;

      const storageStart = Date.now();
      const { error: storagePing } = await supabase.storage.listBuckets();
      const storageTime = Date.now() - storageStart;

      setHealthChecks([
        {
          service: 'Database',
          status: dbPing ? 'down' : dbTime > 2000 ? 'degraded' : 'healthy',
          response_time_ms: dbTime,
          last_checked: new Date().toISOString(),
        },
        {
          service: 'Auth',
          status: authPing ? 'down' : authTime > 2000 ? 'degraded' : 'healthy',
          response_time_ms: authTime,
          last_checked: new Date().toISOString(),
        },
        {
          service: 'Storage',
          status: storagePing ? 'down' : storageTime > 2000 ? 'degraded' : 'healthy',
          response_time_ms: storageTime,
          last_checked: new Date().toISOString(),
        },
      ]);

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [errorFilter, statusFilter]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // =============================================================================
  // ACTIONS
  // =============================================================================

  const markResolved = async (errorId: string) => {
    await supabase
      .from('user_error_log')
      .update({ status: 'resolved', resolved_at: new Date().toISOString() })
      .eq('id', errorId);
    fetchDashboardData();
  };

  const markWontFix = async (errorId: string) => {
    await supabase
      .from('user_error_log')
      .update({ status: 'wont_fix' })
      .eq('id', errorId);
    fetchDashboardData();
  };

  const exportErrorLog = () => {
    const csv = recentErrors.map(error => 
      `${error.created_at},${error.error_type},${error.error_message.replace(/,/g, ';')},${error.page_url || ''},${error.component || ''},${error.status || 'new'}`
    ).join('\n');
    
    const blob = new Blob([`Timestamp,Type,Message,Page,Component,Status\n${csv}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyErrorJson = useCallback((error: RecentError) => {
    const json = JSON.stringify(error, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      toast.success('Error JSON copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  }, []);

  const runDbHealthCheck = async () => {
    setDbHealthLoading(true);
    setDbHealthError(null);
    try {
      const { data, error } = await supabase.functions.invoke('db-health-check');
      if (error) {
        setDbHealthError(error.message || 'Failed to run health check');
        return;
      }
      setDbHealth(data as DbHealthReport);
    } catch (err) {
      setDbHealthError('Failed to connect to health check service');
    } finally {
      setDbHealthLoading(false);
    }
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const getTrend = (today: number, yesterday: number) => {
    if (yesterday === 0) return { direction: 'neutral' as const, percent: 0 };
    const percent = Math.round(((today - yesterday) / yesterday) * 100);
    return {
      direction: (percent > 0 ? 'up' : percent < 0 ? 'down' : 'neutral') as 'up' | 'down' | 'neutral',
      percent: Math.abs(percent),
    };
  };

  const getHealthBadge = (status: string) => {
    switch (status) {
      case 'healthy': return <span className="text-xs px-2 py-1 bg-[#30D158]/20 text-[#30D158] rounded-full font-medium border border-[#30D158]/30">Healthy</span>;
      case 'degraded': return <span className="text-xs px-2 py-1 bg-[#FF9F0A]/20 text-[#FF9F0A] rounded-full font-medium border border-[#FF9F0A]/30">Degraded</span>;
      case 'down': return <span className="text-xs px-2 py-1 bg-[#FF453A]/20 text-[#FF453A] rounded-full font-medium border border-[#FF453A]/30">Down</span>;
      default: return <span className="text-xs px-2 py-1 bg-white/10 text-white/60 rounded-full font-medium">Unknown</span>;
    }
  };

  const getErrorTypeIcon = (type: string) => {
    const iconClass = "w-4 h-4";
    switch (type) {
      case 'page_crash': return <Zap className={`${iconClass} text-[#FF453A]`} />;
      case 'api_failure': return <Server className={`${iconClass} text-[#FF9F0A]`} />;
      case 'auth_issue': return <Users className={`${iconClass} text-[#BF5AF2]`} />;
      case 'media_error': return <Image className={`${iconClass} text-[#FF375F]`} />;
      case 'load_failure': return <Loader className={`${iconClass} text-[#FFD60A]`} />;
      case 'network_error': return <Wifi className={`${iconClass} text-[#0A84FF]`} />;
      default: return <AlertTriangle className={`${iconClass} text-white/40`} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // =============================================================================
  // LOADING STATE
  // =============================================================================

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/20 border-t-[#0A84FF]" />
      </div>
    );
  }

  const trend = todayStats && yesterdayStats 
    ? getTrend(todayStats.total_errors, yesterdayStats.total_errors)
    : { direction: 'neutral' as const, percent: 0 };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#0A84FF]" />
            System
          </h1>
          <p className="text-white/60 mt-1">
            Platform health & error monitoring
          </p>
        </div>
        {activeTab === 'overview' && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/50">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
            <button
              onClick={exportErrorLog}
              disabled={recentErrors.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition-all disabled:opacity-50 border border-white/10"
              title="Export error log as CSV"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => fetchDashboardData()}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition-all disabled:opacity-50 border border-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 p-1 bg-white/5 rounded-xl w-fit border border-white/10">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'overview'
              ? 'bg-[#0A84FF] text-white shadow-lg'
              : 'text-white/60 hover:text-white hover:bg-white/10'
          }`}
        >
          <Activity className="w-4 h-4" /> Overview
        </button>
        <button
          onClick={() => setActiveTab('sentry')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'sentry'
              ? 'bg-[#0A84FF] text-white shadow-lg'
              : 'text-white/60 hover:text-white hover:bg-white/10'
          }`}
        >
          <Bug className="w-4 h-4" /> Sentry Triage
        </button>
      </div>

      {/* Sentry Triage Tab */}
      {activeTab === 'sentry' && <SentryTriagePanel />}

      {/* Overview Tab */}
      {activeTab === 'overview' && (<>


      {/* System Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {healthChecks.map((check) => (
          <div key={check.service} className="p-4 rounded-xl border border-white/10 bg-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {check.status === 'healthy' && <CheckCircle className="w-5 h-5 text-[#30D158]" />}
                {check.status === 'degraded' && <AlertTriangle className="w-5 h-5 text-[#FF9F0A]" />}
                {check.status === 'down' && <XCircle className="w-5 h-5 text-[#FF453A]" />}
                <span className="font-medium text-white">{check.service}</span>
              </div>
              <div className="flex items-center gap-2">
                {check.response_time_ms && (
                  <span className="text-sm text-white/50">{check.response_time_ms}ms</span>
                )}
                {getHealthBadge(check.status)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Database Health Check (deep introspection) */}
      <div className="p-5 rounded-2xl mb-6" style={adminGlassStyles.card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-[#0A84FF]" />
            <h3 className="text-lg font-semibold text-white">Database Health Check</h3>
            {dbHealth && (
              <span className={`text-xs px-2 py-1 rounded-full font-medium border ${
                dbHealth.overall === 'healthy'
                  ? 'bg-[#30D158]/20 text-[#30D158] border-[#30D158]/30'
                  : dbHealth.overall === 'degraded'
                    ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] border-[#FF9F0A]/30'
                    : 'bg-[#FF453A]/20 text-[#FF453A] border-[#FF453A]/30'
              }`}>
                {dbHealth.overall === 'healthy' ? 'All Checks Passed' : dbHealth.overall === 'degraded' ? 'Warnings' : 'Critical Issues'}
              </span>
            )}
          </div>
          <button
            onClick={runDbHealthCheck}
            disabled={dbHealthLoading}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A84FF]/20 hover:bg-[#0A84FF]/30 text-[#0A84FF] rounded-xl font-medium transition-all disabled:opacity-50 border border-[#0A84FF]/30"
          >
            {dbHealthLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Shield className="w-4 h-4" />
            )}
            {dbHealthLoading ? 'Running...' : 'Run Full Check'}
          </button>
        </div>

        {dbHealthError && (
          <div className="p-3 bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-xl text-[#FF453A] text-sm mb-3">
            {dbHealthError}
          </div>
        )}

        {!dbHealth && !dbHealthLoading && !dbHealthError && (
          <p className="text-white/40 text-sm">Click "Run Full Check" to verify schema integrity, RLS enforcement, functions, extensions, triggers, and data integrity.</p>
        )}

        {dbHealth && (
          <div className="space-y-2">
            <p className="text-xs text-white/40 mb-3">
              {dbHealth.postgres_version} &middot; Checked {new Date(dbHealth.timestamp).toLocaleTimeString()}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {dbHealth.checks.map((check) => (
                <div
                  key={check.name}
                  className={`p-3 rounded-xl border ${
                    check.status === 'pass'
                      ? 'border-[#30D158]/20 bg-[#30D158]/5'
                      : check.status === 'warn'
                        ? 'border-[#FF9F0A]/20 bg-[#FF9F0A]/5'
                        : 'border-[#FF453A]/20 bg-[#FF453A]/5'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {check.status === 'pass' && <CheckCircle className="w-4 h-4 text-[#30D158]" />}
                    {check.status === 'warn' && <AlertTriangle className="w-4 h-4 text-[#FF9F0A]" />}
                    {check.status === 'fail' && <XCircle className="w-4 h-4 text-[#FF453A]" />}
                    <span className="text-sm font-medium text-white">
                      {check.name.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {check.details ? (
                    <p className="text-xs text-white/50 line-clamp-2">{check.details}</p>
                  ) : (
                    <p className="text-xs text-white/30">OK</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Total Errors Today */}
        <div className="p-5 rounded-2xl" style={adminGlassStyles.card}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/60 text-sm">Errors Today</span>
            {trend.direction !== 'neutral' && (
              <span className={`flex items-center text-sm ${
                trend.direction === 'up' ? 'text-[#FF453A]' : 'text-[#30D158]'
              }`}>
                {trend.direction === 'up' 
                  ? <TrendingUp className="w-4 h-4 mr-1" /> 
                  : <TrendingDown className="w-4 h-4 mr-1" />
                }
                {trend.percent}%
              </span>
            )}
          </div>
          <p className="text-3xl font-bold text-white">
            {todayStats?.total_errors || 0}
          </p>
          <p className="text-sm text-white/50 mt-1">
            vs {yesterdayStats?.total_errors || 0} yesterday
          </p>
        </div>

        {/* New (Unresolved) */}
        <div className="p-5 rounded-2xl" style={adminGlassStyles.card}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/60 text-sm">Needs Attention</span>
            <AlertCircle className="w-5 h-5 text-[#FF9F0A]" />
          </div>
          <p className="text-3xl font-bold text-[#FF9F0A]">
            {newErrorCount}
          </p>
          <p className="text-sm text-white/50 mt-1">unresolved errors</p>
        </div>

        {/* Users Affected */}
        <div className="p-5 rounded-2xl" style={adminGlassStyles.card}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/60 text-sm">Users Affected</span>
            <Users className="w-5 h-5 text-[#5E5CE6]" />
          </div>
          <p className="text-3xl font-bold text-white">
            {todayStats?.unique_users_affected || 0}
          </p>
          <p className="text-sm text-white/50 mt-1">today</p>
        </div>

        {/* Page Crashes */}
        <div className="p-5 rounded-2xl" style={adminGlassStyles.card}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/60 text-sm">Page Crashes</span>
            <Zap className="w-5 h-5 text-[#FF453A]" />
          </div>
          <p className="text-3xl font-bold text-white">
            {todayStats?.page_crashes || 0}
          </p>
          <p className="text-sm text-white/50 mt-1">critical errors</p>
        </div>
      </div>

      {/* Error Type Breakdown */}
      <div className="p-5 rounded-2xl mb-6" style={adminGlassStyles.card}>
        <h3 className="text-lg font-semibold text-white mb-4">Error Breakdown (Today)</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { type: 'page_crash', label: 'Page Crashes', count: todayStats?.page_crashes || 0, color: '#FF453A' },
            { type: 'api_failure', label: 'API Failures', count: todayStats?.api_failures || 0, color: '#FF9F0A' },
            { type: 'auth_issue', label: 'Auth Issues', count: todayStats?.auth_issues || 0, color: '#BF5AF2' },
            { type: 'media_error', label: 'Media Errors', count: todayStats?.media_errors || 0, color: '#FF375F' },
            { type: 'load_failure', label: 'Load Failures', count: todayStats?.load_failures || 0, color: '#FFD60A' },
            { type: 'network_error', label: 'Network Errors', count: todayStats?.network_errors || 0, color: '#0A84FF' },
          ].map((item) => (
            <button
              key={item.type}
              onClick={() => setErrorFilter(errorFilter === item.type ? 'all' : item.type)}
              className={`p-3 rounded-xl border-2 transition-all ${
                errorFilter === item.type 
                  ? 'border-[#0A84FF] bg-[#0A84FF]/10' 
                  : 'border-transparent bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold" style={{ color: item.color }}>{item.count}</p>
              <p className="text-xs text-white/60 truncate">{item.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Errors List */}
      <div className="rounded-2xl overflow-hidden" style={adminGlassStyles.card}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Recent Errors</h3>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-[#0A84FF]/50 focus:ring-2 focus:ring-[#0A84FF]/20 outline-none"
            >
              <option value="new">New</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="all">All Status</option>
            </select>
          </div>
        </div>
        
        <div className="divide-y divide-white/10">
          {recentErrors.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="w-12 h-12 text-[#30D158] mx-auto mb-3" />
              <p className="text-white font-medium">All Clear</p>
              <p className="text-white/50 text-sm">No errors matching your filters. Great work!</p>
            </div>
          ) : (
            recentErrors.map((error) => (
              <div
                key={error.id}
                className="p-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getErrorTypeIcon(error.error_type)}
                    <div>
                      <p className="text-white font-medium line-clamp-1">
                        {error.error_message}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-white/50">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(error.created_at)}
                        </span>
                        {error.component && (
                          <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full">{error.component}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyErrorJson(error)}
                      className="p-2 text-white/40 hover:text-[#0A84FF] hover:bg-[#0A84FF]/10 rounded-lg transition-colors"
                      title="Copy full error as JSON"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    {error.status === 'new' && (
                      <>
                        <button
                          onClick={() => markResolved(error.id)}
                          className="p-2 text-[#30D158] hover:bg-[#30D158]/10 rounded-lg transition-colors"
                          title="Mark resolved"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => markWontFix(error.id)}
                          className="p-2 text-white/40 hover:bg-white/10 rounded-lg transition-colors"
                          title="Won't fix"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {error.status === 'resolved' && (
                      <span className="text-xs px-2 py-1 bg-[#30D158]/20 text-[#30D158] rounded-full font-medium">Resolved</span>
                    )}
                    {error.status === 'wont_fix' && (
                      <span className="text-xs px-2 py-1 bg-white/10 text-white/60 rounded-full font-medium">Archived</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}

export default SystemDashboard;
